import { MathUtils } from '@skand/three-js-renderer/bin/private/math';
import { Line3, Matrix4, Plane, Quaternion, Vector3, Vector4 } from 'three';
import { BaseCameraMode } from './BaseCameraMode';
/**
 * Camera controls for the Orbit navigation mode. Based on the
 * GlobeCamera implementation in @skand/three-js-renderer.
 *
 * Screen drag - Glide the camera over the globe
 * Ctrl-click - Rotate the camera over a selected pivot
 * Scroll - Zoom in or out
 */
export class CameraOrbit extends BaseCameraMode {
    constructor(renderer, gestures) {
        super(renderer, gestures);
        Object.defineProperty(this, "startTranslationPosition", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "startTranslationPlane", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.startTranslationPosition = new Vector3();
        this.startTranslationPlane = new Plane();
    }
    /**
     * Implement controls when camera is activated (onclick)
     */
    handleCameraActivate(screenPosition) {
        // Set translate start position
        const { position } = this.renderer.pickScreenWorldSpaceSnapping(screenPosition);
        const worldPick = new Vector3(position.x, position.y, position.z);
        const cameraPosition = new Vector3();
        this.camera.getPositionECEF(cameraPosition);
        const direction = worldPick.clone().sub(cameraPosition).normalize();
        const targetDistance = this.calculateTargetDistance(worldPick);
        const targetPosition = cameraPosition.add(direction.multiplyScalar(targetDistance));
        // Deproject the target position to view space
        const worldMatrix = new Matrix4();
        this.camera.getWorldMatrix(worldMatrix);
        targetPosition.applyMatrix4(worldMatrix.invert());
        this.startTranslationPosition.x = targetPosition.x;
        this.startTranslationPosition.y = targetPosition.y;
        this.startTranslationPosition.z = targetPosition.z;
        // Construct view space plane perpendicular to viewing planes
        // Drive camera movement
        this.startTranslationPlane.setFromNormalAndCoplanarPoint(MathUtils.Z_AXIS, this.startTranslationPosition);
    }
    /**
     * Do nothing on camera release
     */
    handleCameraRelease() { }
    /**
     * Do nothing on camera zoom
     */
    handleCameraZoom() { }
    /**
     * Implement controls for gliding the camera over the globe
     */
    handleCameraDrag(screenPosition, prevPosition) {
        if (this.isRotating)
            return;
        // Get NDC on near plane for last cursor position
        const size = this.renderer.getSize();
        const lastDirection = MathUtils.screenCoordsToNDC(prevPosition.x, prevPosition.y, size.x, size.y, new Vector4(0, 0, 0, 1));
        lastDirection.y *= -1;
        // Get NDC on far plane for last cursor position
        const newDirection = MathUtils.screenCoordsToNDC(screenPosition.x, screenPosition.y, size.x, size.y, new Vector4(0, 0, 0, 1));
        newDirection.y *= -1;
        // Get direction to near plane by applying inverse projection and dividing by W
        const invProj = new Matrix4();
        this.camera.getPerspective().getInverseProjectionMatrix(invProj);
        lastDirection.applyMatrix4(invProj);
        lastDirection.divideScalar(lastDirection.w);
        newDirection.applyMatrix4(invProj);
        newDirection.divideScalar(newDirection.w);
        // By defining a ray from the current and last cursor positions we can produce two positions on that plane
        // by intersecting the rays with it. The intersections produce the position on the plane the cursor is
        // currently over and was last over.
        //
        // Because the plane is placed at a distance based on where the cursor was originally clicked the size of
        // the difference between the two intersected points will scale with the distance from what was clicked as
        // well as how far the cursor moved in screen space.
        //
        // By applying this difference to the camera position we can translate the camera along the plane
        // proportional to how far away the clicked surface was yielding us simple, intuitive camera controls that
        // scales movement with distance from the surface.
        const plane = this.startTranslationPlane;
        const length = this.startTranslationPosition.length() * 2;
        const scratchLine = new Line3(MathUtils.ZERO_3.clone());
        // Intersect with initially clicked plane
        scratchLine.end.x = lastDirection.x;
        scratchLine.end.y = lastDirection.y;
        scratchLine.end.z = lastDirection.z;
        scratchLine.end.normalize().multiplyScalar(length);
        const lastIntersect = new Vector3();
        plane.intersectLine(scratchLine, lastIntersect);
        scratchLine.end.x = newDirection.x;
        scratchLine.end.y = newDirection.y;
        scratchLine.end.z = newDirection.z;
        scratchLine.end.normalize().multiplyScalar(length);
        const newIntersect = new Vector3();
        plane.intersectLine(scratchLine, newIntersect);
        // Produce our offset to translate by
        const offset = lastIntersect.sub(newIntersect);
        // Because our offset is defined in view space we need to rotate the offset into view space by applying the
        // camera's rotation
        const rotScratch = new Quaternion();
        this.camera.getRotation(rotScratch);
        offset.applyQuaternion(rotScratch);
        // Apply the translation
        const posScratch = new Vector3();
        this.camera.getPositionECEF(posScratch);
        posScratch.add(offset);
        this.camera.setPositionECEF(posScratch);
    }
}
